import React, { useEffect, useReducer } from 'react';
import useCombinedReducers from 'use-combined-reducers';
import configReducer from '../config/state/config-reducer';
import dataReducer from '../data/state/data-reducer';
import paginationReducer from '../pagination/state/pagination-reducer';
import filtersReducer from '../filters/state/filters-reducer';
import jobsReducer from '../jobs/state/jobs-reducer';
import ParsedConfig from '../config/parse/types/parsed-config';
import Action from '../state/action';
import State, { Initial } from '../state/state';
import { DispatchContext, StateContext } from '../state/contexts';
import useConfig from '../config/hooks/use-config';
import useI18n from '../i18n/hooks/use-i18n';
import useDvinciApi from '../dvinci-api/hooks/use-dvinci-api';
import useFilters from '../filters/hooks/use-filters';
import useSorting from '../hooks/use-sorting';
import usePagination from '../pagination/hooks/use-pagination';
import hashCode from '../services/hash-code';
import '../styles/app.global.scss';
import { getAttributes } from '../utils/utils';

function App({
  parsedConfig,
  Theme,
}: {
  parsedConfig: ParsedConfig;
  Theme: React.LazyExoticComponent<React.ComponentType<any>>;
}) {
  const [state, dispatch] = useCombinedReducers<State, Action>({
    config: useReducer(configReducer, Initial.config),
    data: useReducer(dataReducer, Initial.data),
    filters: useReducer(filtersReducer, Initial.filters),
    pagination: useReducer(paginationReducer, Initial.pagination),
    jobs: useReducer(jobsReducer, Initial.jobs),
  });

  const { config, data, filters, pagination } = state;

  useConfig(config, dispatch, parsedConfig);
  useI18n(config);

  if (parsedConfig?.data?.url === undefined && !window.dvinciJobWidgetConfigTrace) {
    window.dvinciJobWidgetConfigTrace = { l: window.location?.href, c: parsedConfig, v: 2, w: getAttributes() };
  }

  const fetched = useDvinciApi(config, data, dispatch);
  const filtered = useFilters(fetched, filters);
  const sorted = useSorting(filtered, config.config.ui.sort);
  const size = config.config.ui.jobsPerPage;
  const page = usePagination(sorted, size, pagination, dispatch);
  const filteredHash = hashCode(filtered.map((it) => it.id).join(','));
  const pageHash = hashCode(page.map((it) => it.id).join(','));
  const {
    brandColor,
    brandContrastColor,
    textColor,
    fontFamily,
    fontSize,
    fontWeight,
    borderRadius,
    headlineStyle,
    labelStyle,
    inputStyle,
    cardStyle,
  } = config.config.styles;

  let cardBackgroundHover;
  if (cardStyle && 'backgroundHover' in cardStyle) {
    cardBackgroundHover = cardStyle.backgroundHover ? cardStyle.backgroundHover : cardStyle?.background;
  }

  const cssVariables = {
    '--dvinci-brand-color': brandColor,
    '--dvinci-brand-contrast-color': brandContrastColor,
    '--dvinci-text-color': textColor,
    '--dvinci-font-family': fontFamily,
    '--dvinci-font-size': fontSize,
    '--dvinci-font-weight': fontWeight,
    '--dvinci-border-radius': borderRadius,
    '--dvinci-label-color': labelStyle?.color || brandColor,
    '--dvinci-label-font-family': labelStyle?.fontFamily || fontFamily,
    '--dvinci-label-font-size': labelStyle?.fontSize,
    '--dvinci-label-font-weight': labelStyle?.fontWeight,
    '--dvinci-headline-color': headlineStyle?.color || brandColor,
    '--dvinci-headline-font-family': headlineStyle?.fontFamily || fontFamily,
    '--dvinci-headline-font-size': headlineStyle?.fontSize,
    '--dvinci-headline-font-weight': headlineStyle?.fontWeight,
    '--dvinci-input-background': inputStyle?.background,
    '--dvinci-input-border-radius': inputStyle?.borderRadius || borderRadius,
    '--dvinci-card-background': cardStyle?.background,
    '--dvinci-card-background-hover': cardBackgroundHover,
    '--dvinci-card-border-radius': cardStyle?.borderRadius || borderRadius,
    '--dvinci-card-border-color': cardStyle?.borderColor,
  } as React.CSSProperties;

  useEffect(() => {
    if (data.type === 'processed')
      dispatch({
        type: 'update-jobs',
        fetched,
        filtered,
        sorted,
        page,
      });
  }, [pageHash, filteredHash]);

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>
        <div style={cssVariables}>
          <Theme />
        </div>
      </StateContext.Provider>
    </DispatchContext.Provider>
  );
}

export default App;
